/* Copyright (c) 2023 MOCA Systems, Inc. All rights reserved. */

import { Injectable, OnDestroy } from '@angular/core';
import { combineLatest, BehaviorSubject, of } from 'rxjs';
import { debounceTime, delay, switchMap } from 'rxjs/operators';

/**This service is designed to handle state of hover-over
 * Only one hover-over component can be visible at the same moment
 */

@Injectable({
	providedIn: 'root',
})
export class HoverOverStateService implements OnDestroy {
	private _hoverOverTicketIdSubject$ = new BehaviorSubject<string>('');
	public hoverOverTicketId$ = this._hoverOverTicketIdSubject$.asObservable();

	private _cursorOverTicketSubject$ = new BehaviorSubject<boolean>(false);
	public cursorOverTicket$ = this._cursorOverTicketSubject$.asObservable();

	private _cursorOverHoverComponentSubject$ = new BehaviorSubject<boolean>(false);
	public cursorOverHoverComponent$ = this._cursorOverHoverComponentSubject$.asObservable();

	public combinedHoverVisibilityObservable$ = combineLatest([
		this.cursorOverTicket$,
		this.cursorOverHoverComponent$,
	]).pipe(
		debounceTime(100),
		switchMap((visibilityValues) => {
			const isHoverVisible = visibilityValues.some((value) => value);
			if (!isHoverVisible) {
				return of(isHoverVisible).pipe(delay(250));
			}
			return of(isHoverVisible);
		})
	);

	public hoverPosition$ = new BehaviorSubject<{ x: number; y: number }>({ x: 0, y: 0 });

	constructor() {}

	public ngOnDestroy() {
		this._hoverOverTicketIdSubject$.complete();
		this._cursorOverTicketSubject$.complete();
		this._cursorOverHoverComponentSubject$.complete();
	}

	public startHover() {
		this._cursorOverTicketSubject$.next(true);
	}

	public endHover() {
		this._cursorOverTicketSubject$.next(false);
	}

	public startHoverComponent() {
		this._cursorOverHoverComponentSubject$.next(true);
	}

	public endHoverComponent() {
		this._cursorOverHoverComponentSubject$.next(false);
	}

	public updateId(ticketId: string) {
		this._hoverOverTicketIdSubject$.next(ticketId);
	}
}
